import type { Lang } from "@/i18n/translations";
import { LEGAL_PATHS, type LegalDocId } from "@/data/legalPaths";

export type { LegalDocId } from "@/data/legalPaths";
export { legalPath } from "@/data/legalPaths";

export type LegalBlock = {
  heading?: string;
  paragraphs?: string[];
  list?: string[];
};

export type LegalSection = LegalBlock & {
  heading: string;
  subsections?: LegalBlock[];
};

export type LegalDocContent = {
  title: string;
  metaDescription: string;
  updated: string;
  intro: string;
  sections: LegalSection[];
};

type LegalDocDef = {
  id: LegalDocId;
  paths: { sv: string; en: string };
  content: Record<Lang, LegalDocContent>;
};

/** Swedish copy matches intygslakaren-payload-cms page-client files. Updated: 2026-10-19. */
export const LEGAL_DOCS: LegalDocDef[] = [
  {
    id: "privacy",
    paths: LEGAL_PATHS.privacy,
    content: {
      sv: {
        title: "Integritetspolicy",
        metaDescription:
          "Vi värnar om din integritet och behandlar dina personuppgifter med största omsorg och enligt gällande lagar.",
        updated: "2026-10-19",
        intro:
          "Vi värnar om din integritet och behandlar dina personuppgifter med största omsorg och enligt gällande lagar.",
        sections: [
          {
            heading: "Om vår integritetspolicy",
            paragraphs: [
              "AG medical AB (org.nr: 559282-3685) värnar om din integritet och behandlar dina personuppgifter med största omsorg och enligt gällande lagar, inklusive GDPR (General Data Protection Regulation) och Personuppgiftslagen (PuL).",
              "Denna integritetspolicy beskriver hur vi samlar in, använder, lagrar och skyddar dina personuppgifter när du använder vår webbplats eller våra tjänster.",
            ],
          },
          {
            heading: "Vilka personuppgifter samlar vi in?",
            subsections: [
              {
                heading: "Personlig information",
                list: [
                  "Namn och efternamn",
                  "E-postadress",
                  "Telefonnummer",
                  "Personnummer",
                  "Adress",
                  "Födelsedatum",
                ],
              },
              {
                heading: "Medicinsk information",
                list: [
                  "Medicinska journaler",
                  "Undersökningsresultat",
                  "Diagnoser",
                  "Behandlingshistorik",
                  "Läkemedelsinformation",
                ],
              },
            ],
          },
          {
            heading: "Hur använder vi dina personuppgifter?",
            subsections: [
              {
                heading: "Medicinska tjänster",
                paragraphs: [
                  "Vi använder dina personuppgifter för att utföra medicinska undersökningar, utfärda intyg och ge medicinsk rådgivning enligt vår legitimation som läkare.",
                ],
              },
              {
                heading: "Kommunikation",
                paragraphs: [
                  "Vi använder dina kontaktuppgifter för att kommunicera med dig om bokningar, avtalade tider och viktig information om din hälsa.",
                ],
              },
              {
                heading: "Fakturering och administration",
                paragraphs: [
                  "Vi använder dina uppgifter för fakturering, bokföring och andra administrativa uppgifter som krävs för att driva vår verksamhet.",
                ],
              },
              {
                heading: "Juridiska krav",
                paragraphs: [
                  "Vi kan behöva använda dina uppgifter för att uppfylla juridiska krav, såsom rapportering till myndigheter eller försäkringsbolag.",
                ],
              },
            ],
          },
          {
            heading: "Rättslig grund för behandling",
            paragraphs: ["Vi behandlar dina personuppgifter baserat på följande rättsliga grunder:"],
            list: [
              "Samtycke: När du ger ditt uttryckliga samtycke till behandling",
              "Avtal: För att uppfylla vårt avtal med dig om medicinska tjänster",
              "Rättslig skyldighet: För att uppfylla juridiska krav som läkare",
              "Berättigat intresse: För att driva vår verksamhet på ett säkert sätt",
            ],
          },
          {
            heading: "Delning av personuppgifter",
            paragraphs: [
              "Vi delar inte dina personuppgifter med tredje parter utan ditt samtycke, förutom i följande fall:",
            ],
            subsections: [
              {
                heading: "Myndigheter och försäkringsbolag",
                paragraphs: ["När det krävs enligt lag eller för att uppfylla försäkringskrav."],
              },
              {
                heading: "Andra läkare och vårdgivare",
                paragraphs: ["När det är nödvändigt för din vård och behandling."],
              },
              {
                heading: "IT-leverantörer",
                paragraphs: ["För att säkerställa säker lagring och hantering av dina uppgifter."],
              },
            ],
          },
          {
            heading: "Datasäkerhet",
            subsections: [
              {
                heading: "Tekniska åtgärder",
                list: [
                  "Kryptering av data",
                  "Säker databaslagring",
                  "Regelbundna säkerhetsuppdateringar",
                  "Säkerhetskopiering",
                ],
              },
              {
                heading: "Organisatoriska åtgärder",
                list: [
                  "Personalutbildning i datasäkerhet",
                  "Åtkomstkontroll",
                  "Sekretessavtal",
                  "Regelbundna säkerhetsgranskningar",
                ],
              },
            ],
          },
          {
            heading: "Dina rättigheter",
            subsections: [
              {
                heading: "Rätt till information",
                paragraphs: ["Du har rätt att få information om hur vi behandlar dina personuppgifter."],
              },
              {
                heading: "Rätt till åtkomst",
                paragraphs: ["Du har rätt att få en kopia av dina personuppgifter."],
              },
              {
                heading: "Rätt till rättelse",
                paragraphs: ["Du har rätt att få felaktiga uppgifter rättade."],
              },
              {
                heading: "Rätt till radering",
                paragraphs: [
                  "Du har rätt att få dina personuppgifter raderade under vissa omständigheter.",
                ],
              },
              {
                heading: "Rätt till begränsning",
                paragraphs: ["Du har rätt att begränsa behandlingen av dina personuppgifter."],
              },
              {
                heading: "Rätt till dataportabilitet",
                paragraphs: ["Du har rätt att få dina uppgifter överförda i ett strukturerat format."],
              },
              {
                heading: "Rätt att invända",
                paragraphs: ["Du har rätt att invända mot behandling baserad på berättigat intresse."],
              },
            ],
          },
          {
            heading: "Kontakt för integritetsfrågor",
            paragraphs: [
              "Om du har frågor om denna integritetspolicy eller vill utöva dina rättigheter, kontakta oss på:",
              "E-post: info@intygslakaren.se",
              "Telefon: 031-761 77 00",
              "Adress: Järntorgsgatan 8, 413 30 Göteborg, Sverige",
              "Organisationsnummer: 559282-3685",
              "Du har också rätt att lämna klagomål till Integritetsskyddsmyndigheten (IMY) om du anser att vi behandlar dina personuppgifter på ett felaktigt sätt.",
            ],
          },
          {
            heading: "Uppdateringar av denna policy",
            paragraphs: [
              "Vi kan komma att uppdatera denna integritetspolicy från tid till annan. Eventuella ändringar kommer att publiceras på denna sida med ett uppdaterat datum. Vi rekommenderar att du regelbundet läser igenom denna policy för att hålla dig informerad om hur vi skyddar dina personuppgifter.",
            ],
          },
          {
            heading: "Webbanalys och annonspixlar",
            paragraphs: [
              "När du godkänner analytiska respektive marknadsföringscookies använder vi Google Tag Manager (GTM-PQ2HC24) för att ladda Google Analytics 4 (GA4) och annons-/konverteringspixlar (t.ex. Meta Pixel), samt Microsoft Clarity för heatmaps och sessionsanalys. Behandlingen sker med stöd av ditt samtycke. Läs mer i vår cookie-policy.",
            ],
          },
        ],
      },
      en: {
        title: "Privacy policy",
        metaDescription:
          "We care about your privacy and process your personal data with care and in accordance with applicable law.",
        updated: "2026-10-19",
        intro:
          "We care about your privacy and process your personal data with care and in accordance with applicable law.",
        sections: [
          {
            heading: "About our privacy policy",
            paragraphs: [
              "AG medical AB (org. no. 559282-3685) cares about your privacy and processes your personal data with the greatest care and in accordance with applicable laws, including the GDPR (General Data Protection Regulation) and the Swedish Personal Data Act (PuL).",
              "This privacy policy describes how we collect, use, store and protect your personal data when you use our website or our services.",
            ],
          },
          {
            heading: "What personal data do we collect?",
            subsections: [
              {
                heading: "Personal information",
                list: [
                  "First and last name",
                  "Email address",
                  "Phone number",
                  "Personal identity number",
                  "Address",
                  "Date of birth",
                ],
              },
              {
                heading: "Medical information",
                list: [
                  "Medical records",
                  "Examination results",
                  "Diagnoses",
                  "Treatment history",
                  "Medication information",
                ],
              },
            ],
          },
          {
            heading: "How do we use your personal data?",
            subsections: [
              {
                heading: "Medical services",
                paragraphs: [
                  "We use your personal data to carry out medical examinations, issue certificates and provide medical advice under our licence as doctors.",
                ],
              },
              {
                heading: "Communication",
                paragraphs: [
                  "We use your contact details to communicate with you about bookings, appointments and important information about your health.",
                ],
              },
              {
                heading: "Invoicing and administration",
                paragraphs: [
                  "We use your data for invoicing, accounting and other administrative tasks required to run our business.",
                ],
              },
              {
                heading: "Legal requirements",
                paragraphs: [
                  "We may need to use your data to meet legal requirements, such as reporting to authorities or insurance companies.",
                ],
              },
            ],
          },
          {
            heading: "Legal basis for processing",
            paragraphs: ["We process your personal data based on the following legal bases:"],
            list: [
              "Consent: When you give your express consent to processing",
              "Contract: To fulfil our contract with you for medical services",
              "Legal obligation: To fulfil legal requirements as doctors",
              "Legitimate interest: To run our business in a secure way",
            ],
          },
          {
            heading: "Sharing of personal data",
            paragraphs: [
              "We do not share your personal data with third parties without your consent, except in the following cases:",
            ],
            subsections: [
              {
                heading: "Authorities and insurance companies",
                paragraphs: ["When required by law or to meet insurance requirements."],
              },
              {
                heading: "Other doctors and care providers",
                paragraphs: ["When necessary for your care and treatment."],
              },
              {
                heading: "IT providers",
                paragraphs: ["To ensure secure storage and handling of your data."],
              },
            ],
          },
          {
            heading: "Data security",
            subsections: [
              {
                heading: "Technical measures",
                list: [
                  "Encryption of data",
                  "Secure database storage",
                  "Regular security updates",
                  "Backups",
                ],
              },
              {
                heading: "Organisational measures",
                list: [
                  "Staff training in data security",
                  "Access control",
                  "Confidentiality agreements",
                  "Regular security reviews",
                ],
              },
            ],
          },
          {
            heading: "Your rights",
            subsections: [
              {
                heading: "Right to information",
                paragraphs: ["You have the right to information about how we process your personal data."],
              },
              {
                heading: "Right of access",
                paragraphs: ["You have the right to receive a copy of your personal data."],
              },
              {
                heading: "Right to rectification",
                paragraphs: ["You have the right to have inaccurate data corrected."],
              },
              {
                heading: "Right to erasure",
                paragraphs: [
                  "You have the right to have your personal data erased under certain circumstances.",
                ],
              },
              {
                heading: "Right to restriction",
                paragraphs: ["You have the right to restrict the processing of your personal data."],
              },
              {
                heading: "Right to data portability",
                paragraphs: ["You have the right to receive your data transferred in a structured format."],
              },
              {
                heading: "Right to object",
                paragraphs: ["You have the right to object to processing based on legitimate interest."],
              },
            ],
          },
          {
            heading: "Contact for privacy questions",
            paragraphs: [
              "If you have questions about this privacy policy or wish to exercise your rights, contact us at:",
              "Email: info@intygslakaren.se",
              "Phone: 031-761 77 00",
              "Address: Järntorgsgatan 8, 413 30 Gothenburg, Sweden",
              "Organisation number: 559282-3685",
              "You also have the right to lodge a complaint with the Swedish Authority for Privacy Protection (IMY) if you believe we process your personal data incorrectly.",
            ],
          },
          {
            heading: "Updates to this policy",
            paragraphs: [
              "We may update this privacy policy from time to time. Any changes will be published on this page with an updated date. We recommend that you review this policy regularly to stay informed about how we protect your personal data.",
            ],
          },
          {
            heading: "Web analytics and advertising pixels",
            paragraphs: [
              "When you accept analytics and/or marketing cookies, we use Google Tag Manager (GTM-PQ2HC24) to load Google Analytics 4 (GA4) and advertising/conversion pixels (e.g. Meta Pixel), as well as Microsoft Clarity for heatmaps and session analysis. Processing is based on your consent. See our cookie policy for more information.",
            ],
          },
        ],
      },
    },
  },
  {
    id: "cookies",
    paths: LEGAL_PATHS.cookies,
    content: {
      sv: {
        title: "Cookie-policy",
        metaDescription: "Information om hur vi använder cookies på vår webbplats",
        updated: "2026-10-19",
        intro: "Information om hur vi använder cookies på vår webbplats",
        sections: [
          {
            heading: "Vad är cookies?",
            paragraphs: [
              "Cookies är små textfiler som lagras på din enhet när du besöker vår webbplats. De hjälper oss att förbättra din upplevelse, analysera trafik och personalisera innehåll.",
              "Vi på Intygsläkaren tar din integritet på största allvar och följer GDPR-regler för hantering av personuppgifter och cookies.",
            ],
          },
          {
            heading: "Vilka typer av cookies använder vi?",
            subsections: [
              {
                heading: "Nödvändiga cookies",
                paragraphs: [
                  "Dessa cookies är nödvändiga för att webbplatsen ska fungera korrekt. De kan inte inaktiveras och lagras automatiskt på din enhet.",
                ],
                list: ["Sessionshantering", "Säkerhetsfunktioner", "Grundläggande webbplatsfunktioner"],
              },
              {
                heading: "Analytiska cookies",
                paragraphs: [
                  "Hjälper oss att förstå hur besökare interagerar med vår webbplats genom att samla och rapportera information anonymt.",
                ],
                list: ["Webbplatsstatistik", "Användarinteraktioner", "Prestandaövervakning"],
              },
              {
                heading: "Funktionella cookies",
                paragraphs: [
                  "Aktiverar förbättrade funktioner och personalisering, som att komma ihåg dina preferenser och inställningar.",
                ],
                list: ["Språkval", "Användarinställningar", "Personaliserat innehåll"],
              },
              {
                heading: "Marknadsföringscookies",
                paragraphs: [
                  "Används för att visa relevanta annonser och mäta effektiviteten av våra marknadsföringskampanjer.",
                ],
                list: ["Målriktad annonsering", "Kampanjspårning", "Sociala medier-integration"],
              },
            ],
          },
          {
            heading: "Google Analytics 4 och annonspixlar",
            paragraphs: [
              "När du godkänner analytiska cookies laddas Google Analytics 4 (GA4) via Google Tag Manager (GTM-PQ2HC24) samt Microsoft Clarity (heatmaps/sessionsanalys). När du godkänner marknadsföringscookies kan Tag Manager även ladda annons- och konverteringspixlar, till exempel Meta Pixel. Dessa verktyg aktiveras först efter ditt samtycke.",
            ],
          },
          {
            heading: "Hantera dina cookie-inställningar",
            paragraphs: [
              "Du kan när som helst ändra dina cookie-inställningar. Observera att inaktivering av vissa cookies kan påverka funktionaliteten på vår webbplats.",
            ],
          },
          {
            heading: "Hantera cookies i din webbläsare",
            paragraphs: [
              "Du kan också hantera cookies direkt i din webbläsare. Här är instruktioner för de vanligaste webbläsarna:",
            ],
            subsections: [
              {
                heading: "Google Chrome",
                paragraphs: ["Inställningar → Sekretess och säkerhet → Cookies och andra webbplatsdata"],
              },
              {
                heading: "Mozilla Firefox",
                paragraphs: ["Inställningar → Sekretess och säkerhet → Cookies och webbplatsdata"],
              },
              {
                heading: "Safari",
                paragraphs: ["Inställningar → Sekretess → Hantera webbplatsdata"],
              },
              {
                heading: "Microsoft Edge",
                paragraphs: ["Inställningar → Cookies och webbplatsbehörigheter"],
              },
            ],
          },
        ],
      },
      en: {
        title: "Cookie policy",
        metaDescription: "Information about how we use cookies on our website",
        updated: "2026-10-19",
        intro: "Information about how we use cookies on our website",
        sections: [
          {
            heading: "What are cookies?",
            paragraphs: [
              "Cookies are small text files stored on your device when you visit our website. They help us improve your experience, analyse traffic and personalise content.",
              "At Intygsläkaren we take your privacy very seriously and follow GDPR rules for handling personal data and cookies.",
            ],
          },
          {
            heading: "What types of cookies do we use?",
            subsections: [
              {
                heading: "Necessary cookies",
                paragraphs: [
                  "These cookies are necessary for the website to function correctly. They cannot be disabled and are stored automatically on your device.",
                ],
                list: ["Session management", "Security features", "Basic website functions"],
              },
              {
                heading: "Analytics cookies",
                paragraphs: [
                  "Help us understand how visitors interact with our website by collecting and reporting information anonymously.",
                ],
                list: ["Website statistics", "User interactions", "Performance monitoring"],
              },
              {
                heading: "Functional cookies",
                paragraphs: [
                  "Enable enhanced features and personalisation, such as remembering your preferences and settings.",
                ],
                list: ["Language choice", "User settings", "Personalised content"],
              },
              {
                heading: "Marketing cookies",
                paragraphs: [
                  "Used to show relevant ads and measure the effectiveness of our marketing campaigns.",
                ],
                list: ["Targeted advertising", "Campaign tracking", "Social media integration"],
              },
            ],
          },
          {
            heading: "Google Analytics 4 and advertising pixels",
            paragraphs: [
              "When you accept analytics cookies, Google Analytics 4 (GA4) is loaded via Google Tag Manager (GTM-PQ2HC24) and Microsoft Clarity (heatmaps/session analysis). When you accept marketing cookies, Tag Manager may also load advertising and conversion pixels, for example Meta Pixel. These tools are activated only after your consent.",
            ],
          },
          {
            heading: "Manage your cookie settings",
            paragraphs: [
              "You can change your cookie settings at any time. Note that disabling some cookies may affect the functionality of our website.",
            ],
          },
          {
            heading: "Manage cookies in your browser",
            paragraphs: [
              "You can also manage cookies directly in your browser. Here are instructions for the most common browsers:",
            ],
            subsections: [
              {
                heading: "Google Chrome",
                paragraphs: ["Settings → Privacy and security → Cookies and other site data"],
              },
              {
                heading: "Mozilla Firefox",
                paragraphs: ["Settings → Privacy & Security → Cookies and Site Data"],
              },
              {
                heading: "Safari",
                paragraphs: ["Settings → Privacy → Manage Website Data"],
              },
              {
                heading: "Microsoft Edge",
                paragraphs: ["Settings → Cookies and site permissions"],
              },
            ],
          },
        ],
      },
    },
  },
  {
    id: "terms",
    paths: LEGAL_PATHS.terms,
    content: {
      sv: {
        title: "Användarvillkor",
        metaDescription:
          "Dessa villkor reglerar användningen av våra tjänster och webbplats. Genom att använda våra tjänster godkänner du dessa villkor.",
        updated: "2026-10-19",
        intro:
          "Dessa villkor reglerar användningen av våra tjänster och webbplats. Genom att använda våra tjänster godkänner du dessa villkor.",
        sections: [
          {
            heading: "Om dessa användarvillkor",
            paragraphs: [
              'Dessa användarvillkor ("Villkor") reglerar användningen av tjänster som erbjuds av AG medical AB (org.nr: 559282-3685) ("Vi", "Oss", "Vår", "Bolaget") genom vår webbplats och våra medicinska tjänster.',
              "Genom att använda våra tjänster, boka tid eller interagera med vår webbplats godkänner du dessa villkor. Om du inte godkänner dessa villkor, får du inte använda våra tjänster.",
            ],
          },
          {
            heading: "Företagsinformation",
            subsections: [
              {
                heading: "Bolagsinformation",
                list: [
                  "Företagsnamn: AG medical AB",
                  "Organisationsnummer: 559282-3685",
                  "Adress: Järntorgsgatan 8, 413 30 Göteborg, Sverige",
                ],
              },
              {
                heading: "Kontaktinformation",
                list: [
                  "E-post: info@intygslakaren.se",
                  "Telefon: 031-761 77 00",
                  "Webbplats: www.intygslakaren.se",
                ],
              },
            ],
          },
          {
            heading: "Våra tjänster",
            subsections: [
              {
                heading: "Medicinska tjänster",
                paragraphs: [
                  "Vi erbjuder medicinska undersökningar och utfärdar olika typer av intyg, inklusive körkortsintyg, arbetsintyg, sjukintyg och friskintyg. Alla tjänster utförs av legitimerade läkare enligt svensk lag.",
                ],
              },
              {
                heading: "Bokning och konsultation",
                paragraphs: [
                  "Vi erbjuder möjlighet att boka tider för medicinska undersökningar och konsultationer via vår webbplats eller telefon.",
                ],
              },
              {
                heading: "Information och rådgivning",
                paragraphs: [
                  "Vi tillhandahåller information om våra tjänster och medicinsk rådgivning genom vår webbplats och direktkontakt.",
                ],
              },
            ],
          },
          {
            heading: "Dina skyldigheter som användare",
            subsections: [
              {
                heading: "Korrekt information",
                paragraphs: [
                  "Du är skyldig att tillhandahålla korrekt och fullständig information när du bokar tid eller använder våra tjänster. Felaktig information kan påverka kvaliteten på din vård.",
                ],
              },
              {
                heading: "Punktlighet",
                paragraphs: [
                  "Du är skyldig att komma i tid till dina avtalade tider. Sen ankomst kan resultera i att din tid måste ombokas.",
                ],
              },
              {
                heading: "Respektfullt beteende",
                paragraphs: [
                  "Du ska uppföra dig respektfullt mot vår personal och andra patienter. Störande beteende kan resultera i att du nekas fortsatt service.",
                ],
              },
              {
                heading: "Följa instruktioner",
                paragraphs: ["Du ska följa medicinska instruktioner och råd som ges av vår personal."],
              },
            ],
          },
          {
            heading: "Bokning och avbokning",
            subsections: [
              {
                heading: "Bokning",
                list: [
                  "Bokningar görs via webbplats eller telefon",
                  "Bekräftelse skickas via e-post eller SMS",
                  "Du får bokningsnummer för referens",
                  "Bokningar är bindande från bekräftelse",
                ],
              },
              {
                heading: "Avbokning",
                list: [
                  "Avbokning minst 24 timmar i förväg",
                  "Sen avbokning kan debiteras",
                  "Avbokning via telefon eller e-post",
                  "Ombokning möjlig vid förhinder",
                ],
              },
            ],
          },
          {
            heading: "Betalningsvillkor",
            subsections: [
              {
                heading: "Betalningsmetoder",
                paragraphs: ["Vi accepterar följande betalningsmetoder:"],
                list: ["Kortbetalning (Visa, Mastercard)", "Klarna", "Swish"],
              },
              {
                heading: "Betalningstid",
                paragraphs: [
                  "Betalning sker vanligtvis vid besöket eller inom 30 dagar från fakturering. För sena betalningar kan dröjsmålsränta tillkomma enligt lag.",
                ],
              },
              {
                heading: "Återbetalning",
                paragraphs: [
                  "Återbetalning kan göras om tjänsten inte kan levereras på grund av oss. Återbetalning görs inom 14 dagar från godkännande.",
                ],
              },
            ],
          },
          {
            heading: "Ansvar och förbehåll",
            subsections: [
              {
                heading: "Medicinskt ansvar",
                paragraphs: [
                  "Våra tjänster utförs av legitimerade läkare enligt svensk lag. Vi följer etiska riktlinjer och medicinska standarder. Dock kan vi inte garantera specifika medicinska resultat.",
                ],
              },
              {
                heading: "Begränsning av ansvar",
                paragraphs: [
                  "Vårt ansvar är begränsat till kostnaden för den specifika tjänst som utförts. Vi ansvarar inte för indirekta skador eller följdskador som kan uppstå.",
                ],
              },
              {
                heading: "Tredje parters tjänster",
                paragraphs: [
                  "Vi ansvarar inte för tjänster eller produkter från tredje parter som kan rekommenderas eller länkas till från vår webbplats.",
                ],
              },
            ],
          },
          {
            heading: "Integritet och personuppgifter",
            paragraphs: [
              "Vi behandlar dina personuppgifter enligt vår integritetspolicy och gällande lagar. Som medicinsk verksamhet följer vi strikta sekretessregler.",
            ],
            list: [
              "All medicinsk information behandlas konfidentiellt",
              "Personuppgifter lagras säkert enligt GDPR",
              "Du har rätt att få information om dina uppgifter",
              "Sekretess gäller även efter avslutad behandling",
            ],
          },
          {
            heading: "Immateriella rättigheter",
            subsections: [
              {
                heading: "Webbplatsinnehåll",
                paragraphs: [
                  "Allt innehåll på vår webbplats, inklusive texter, bilder och logotyper, är skyddat av upphovsrätt och tillhör AG medical AB eller våra licensgivare.",
                ],
              },
              {
                heading: "Användning",
                paragraphs: [
                  "Du får inte kopiera, distribuera eller modifiera innehållet utan vår skriftliga tillåtelse. Kommersiell användning är förbjuden.",
                ],
              },
            ],
          },
          {
            heading: "Ändringar av villkoren",
            paragraphs: [
              "Vi förbehåller oss rätten att ändra dessa användarvillkor när som helst. Ändringar kommer att publiceras på denna sida med ett uppdaterat datum.",
              "Fortsatt användning av våra tjänster efter ändringar innebär att du godkänner de nya villkoren.",
            ],
          },
          {
            heading: "Tillämplig lag och tvistlösning",
            paragraphs: [
              "Dessa villkor regleras av svensk lag. Eventuella tvister ska lösas genom förhandling eller genom svenska domstolar.",
              "För konsumenttvister finns möjlighet att vända sig till Allmänna reklamationsnämnden eller Konsumentverket.",
            ],
          },
          {
            heading: "Kontakt",
            paragraphs: [
              "Om du har frågor om dessa användarvillkor, kontakta oss:",
              "E-post: info@intygslakaren.se",
              "Telefon: 031-761 77 00",
              "Adress: Järntorgsgatan 8, 413 30 Göteborg, Sverige",
            ],
          },
        ],
      },
      en: {
        title: "Terms of use",
        metaDescription:
          "These terms govern the use of our services and website. By using our services you accept these terms.",
        updated: "2026-10-19",
        intro:
          "These terms govern the use of our services and website. By using our services you accept these terms.",
        sections: [
          {
            heading: "About these terms of use",
            paragraphs: [
              'These terms of use ("Terms") govern the use of services offered by AG medical AB (org. no. 559282-3685) ("We", "Us", "Our", "the Company") through our website and our medical services.',
              "By using our services, booking an appointment or interacting with our website you accept these terms. If you do not accept these terms, you may not use our services.",
            ],
          },
          {
            heading: "Company information",
            subsections: [
              {
                heading: "Company details",
                list: [
                  "Company name: AG medical AB",
                  "Organisation number: 559282-3685",
                  "Address: Järntorgsgatan 8, 413 30 Gothenburg, Sweden",
                ],
              },
              {
                heading: "Contact information",
                list: [
                  "Email: info@intygslakaren.se",
                  "Phone: 031-761 77 00",
                  "Website: www.intygslakaren.se",
                ],
              },
            ],
          },
          {
            heading: "Our services",
            subsections: [
              {
                heading: "Medical services",
                paragraphs: [
                  "We offer medical examinations and issue various types of certificates, including driving licence certificates, work certificates, sick notes and fitness certificates. All services are provided by licensed doctors under Swedish law.",
                ],
              },
              {
                heading: "Booking and consultation",
                paragraphs: [
                  "We offer the option to book appointments for medical examinations and consultations via our website or by phone.",
                ],
              },
              {
                heading: "Information and advice",
                paragraphs: [
                  "We provide information about our services and medical advice through our website and direct contact.",
                ],
              },
            ],
          },
          {
            heading: "Your obligations as a user",
            subsections: [
              {
                heading: "Accurate information",
                paragraphs: [
                  "You must provide accurate and complete information when booking or using our services. Incorrect information may affect the quality of your care.",
                ],
              },
              {
                heading: "Punctuality",
                paragraphs: [
                  "You must arrive on time for your appointments. Late arrival may result in the appointment needing to be rescheduled.",
                ],
              },
              {
                heading: "Respectful behaviour",
                paragraphs: [
                  "You must behave respectfully towards our staff and other patients. Disruptive behaviour may result in refusal of further service.",
                ],
              },
              {
                heading: "Follow instructions",
                paragraphs: ["You must follow medical instructions and advice given by our staff."],
              },
            ],
          },
          {
            heading: "Booking and cancellation",
            subsections: [
              {
                heading: "Booking",
                list: [
                  "Bookings are made via the website or phone",
                  "Confirmation is sent by email or SMS",
                  "You receive a booking reference",
                  "Bookings are binding from confirmation",
                ],
              },
              {
                heading: "Cancellation",
                list: [
                  "Cancel at least 24 hours in advance",
                  "Late cancellation may be charged",
                  "Cancel by phone or email",
                  "Rescheduling is possible if you are prevented from attending",
                ],
              },
            ],
          },
          {
            heading: "Payment terms",
            subsections: [
              {
                heading: "Payment methods",
                paragraphs: ["We accept the following payment methods:"],
                list: ["Card payment (Visa, Mastercard)", "Klarna", "Swish"],
              },
              {
                heading: "Payment timing",
                paragraphs: [
                  "Payment is usually made at the visit or within 30 days of invoicing. Late payments may incur statutory interest.",
                ],
              },
              {
                heading: "Refunds",
                paragraphs: [
                  "A refund may be made if the service cannot be delivered due to us. Refunds are made within 14 days of approval.",
                ],
              },
            ],
          },
          {
            heading: "Liability and disclaimers",
            subsections: [
              {
                heading: "Medical responsibility",
                paragraphs: [
                  "Our services are provided by licensed doctors under Swedish law. We follow ethical guidelines and medical standards. However, we cannot guarantee specific medical outcomes.",
                ],
              },
              {
                heading: "Limitation of liability",
                paragraphs: [
                  "Our liability is limited to the cost of the specific service provided. We are not liable for indirect or consequential loss that may arise.",
                ],
              },
              {
                heading: "Third-party services",
                paragraphs: [
                  "We are not responsible for services or products from third parties that may be recommended or linked from our website.",
                ],
              },
            ],
          },
          {
            heading: "Privacy and personal data",
            paragraphs: [
              "We process your personal data according to our privacy policy and applicable law. As a medical practice we follow strict confidentiality rules.",
            ],
            list: [
              "All medical information is treated confidentially",
              "Personal data is stored securely under the GDPR",
              "You have the right to information about your data",
              "Confidentiality continues after treatment ends",
            ],
          },
          {
            heading: "Intellectual property",
            subsections: [
              {
                heading: "Website content",
                paragraphs: [
                  "All content on our website, including text, images and logos, is protected by copyright and belongs to AG medical AB or our licensors.",
                ],
              },
              {
                heading: "Use",
                paragraphs: [
                  "You may not copy, distribute or modify the content without our written permission. Commercial use is prohibited.",
                ],
              },
            ],
          },
          {
            heading: "Changes to the terms",
            paragraphs: [
              "We reserve the right to change these terms of use at any time. Changes will be published on this page with an updated date.",
              "Continued use of our services after changes means you accept the new terms.",
            ],
          },
          {
            heading: "Governing law and disputes",
            paragraphs: [
              "These terms are governed by Swedish law. Any disputes shall be resolved by negotiation or by Swedish courts.",
              "For consumer disputes you may turn to the National Board for Consumer Disputes (ARN) or the Swedish Consumer Agency.",
            ],
          },
          {
            heading: "Contact",
            paragraphs: [
              "If you have questions about these terms of use, contact us:",
              "Email: info@intygslakaren.se",
              "Phone: 031-761 77 00",
              "Address: Järntorgsgatan 8, 413 30 Gothenburg, Sweden",
            ],
          },
        ],
      },
    },
  },
];

export function getLegalDoc(id: LegalDocId): LegalDocDef {
  const doc = LEGAL_DOCS.find((d) => d.id === id);
  if (!doc) throw new Error(`Unknown legal doc: ${id}`);
  return doc;
}

export function getLegalDocByPath(pathname: string): LegalDocDef | null {
  const normalized = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  return LEGAL_DOCS.find((d) => d.paths.sv === normalized || d.paths.en === normalized) ?? null;
}
